import { __extends } from "tslib";
import { ASObject } from '@awayfl/avm2';
import { Matrix as AwayMatrix } from '@awayjs/core';
var Matrix = /** @class */ (function (_super) {
    __extends(Matrix, _super);
    /**
   * Creates a new Matrix object with the specified parameters. In matrix
   * notation, the properties are organized like this:
   *
   * <p>If you do not provide any parameters to the <code>new Matrix()</code>
   * constructor, it creates an <i>identity matrix</i> with the following
   * values:</p>
   *
   * <p>In matrix notation, the identity matrix looks like this:</p>
   *
   * @param a  The value that affects the positioning of pixels along the
   *           <i>x</i> axis when scaling or rotating an image.
   * @param b  The value that affects the positioning of pixels along the
   *           <i>y</i> axis when rotating or skewing an image.
   * @param c  The value that affects the positioning of pixels along the
   *           <i>x</i> axis when rotating or skewing an image.
   * @param d  The value that affects the positioning of pixels along the
   *           <i>y</i> axis when scaling or rotating an image..
   * @param tx The distance by which to translate each point along the <i>x</i>
   *           axis.
   * @param ty The distance by which to translate each point along the <i>y</i>
   *           axis.
   */
    function Matrix(aAdaptee, b, c, d, tx, ty) {
        if (aAdaptee === void 0) { aAdaptee = 1; }
        if (b === void 0) { b = 0; }
        if (c === void 0) { c = 0; }
        if (d === void 0) { d = 1; }
        if (tx === void 0) { tx = 0; }
        if (ty === void 0) { ty = 0; }
        var _this = _super.call(this) || this;
        _this._adaptee = (aAdaptee instanceof AwayMatrix) ? aAdaptee : new AwayMatrix(+aAdaptee, +b, +c, +d, +tx, +ty);
        return _this;
    }
    Matrix.classInitializer = function () {
        this.FROZEN_IDENTITY_MATRIX = Object.freeze(this.axConstruct([]));
        this.TEMP_MATRIX = this.axConstruct([]);
    };
    Object.defineProperty(Matrix.prototype, "adaptee", {
        // "invert", "identity", "createBox",
        // "createGradientBox", "rotate", "translate",
        // "scale", "deltaTransformPoint", "transformPoint",
        // "copyFrom", "setTo", "copyRowTo", "copyColumnTo",
        // "copyRowFrom", "copyColumnFrom", "clone",
        // "toString"];
        get: function () {
            return this._adaptee;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Matrix.prototype, "a", {
        /**
       * The value that affects the positioning of pixels along the <i>x</i> axis
       * when scaling or rotating an image.
       */
        get: function () {
            return this._adaptee.rawData[0];
        },
        set: function (value) {
            this._adaptee.rawData[0] = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Matrix.prototype, "b", {
        /**
       * The value that affects the positioning of pixels along the <i>y</i> axis
       * when rotating or skewing an image.
       */
        get: function () {
            return this._adaptee.rawData[1];
        },
        set: function (value) {
            this._adaptee.rawData[1] = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Matrix.prototype, "c", {
        /**
       * The value that affects the positioning of pixels along the <i>x</i> axis
       * when rotating or skewing an image.
       */
        get: function () {
            return this._adaptee.rawData[2];
        },
        set: function (value) {
            this._adaptee.rawData[2] = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Matrix.prototype, "d", {
        /**
       * The value that affects the positioning of pixels along the <i>y</i> axis
       * when scaling or rotating an image.
       */
        get: function () {
            return this._adaptee.rawData[3];
        },
        set: function (value) {
            this._adaptee.rawData[3] = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Matrix.prototype, "tx", {
        /**
       * The distance by which to translate each point along the <i>x</i> axis.
       */
        get: function () {
            return this._adaptee.rawData[4];
        },
        set: function (value) {
            this._adaptee.rawData[4] = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Matrix.prototype, "ty", {
        /**
       * The distance by which to translate each point along the <i>y</i> axis.
       */
        get: function () {
            return this._adaptee.rawData[5];
        },
        set: function (value) {
            this._adaptee.rawData[5] = value;
        },
        enumerable: false,
        configurable: true
    });
    Matrix.FromUntyped = function (object) {
        return new this.sec.flash.geom.Matrix(object.a, object.b, object.c, object.d, object.tx, object.ty);
    };
    // Keep in sync with writeExternal below!
    Matrix.FromDataBuffer = function (input) {
        return new this.sec.flash.geom.Matrix(input.readFloat(), input.readFloat(), input.readFloat(), input.readFloat(), input.readFloat(), input.readFloat());
    };
    /**
   * Concatenates a matrix with the current matrix, effectively combining the
   * geometric effects of the two. In mathematical terms, concatenating two
   * matrixes is the same as combining them using matrix multiplication.
   *
   * <p>For example, if matrix <code>m1</code> scales an object by a factor of
   * four, and matrix <code>m2</code> rotates an object by 1.5707963267949
   * radians(<code>Math.PI/2</code>), then <code>m1.concat(m2)</code>
   * transforms <code>m1</code> into a matrix that scales an object by a factor
   * of four and rotates the object by <code>Math.PI/2</code> radians. </p>
   *
   * <p>This method replaces the source matrix with the concatenated matrix. If
   * you want to concatenate two matrixes without altering either of the two
   * source matrixes, first copy the source matrix by using the
   * <code>clone()</code> method, as shown in the Class Examples section.</p>
   *
   * @param matrix The matrix to be concatenated to the source matrix.
   */
    Matrix.prototype.concat = function (other) {
        this._adaptee.concat(other.adaptee);
    };
    /**
   * Performs the opposite transformation of the original matrix. You can apply
   * an inverted matrix to an object to undo the transformation performed when
   * applying the original matrix.
   */
    Matrix.prototype.invert = function () {
        this._adaptee.invert();
    };
    /**
   * Sets each matrix property to a value that causes a null transformation. An
   * object transformed by applying an identity matrix will be identical to the
   * original.
   *
   * <p>After calling the <code>identity()</code> method, the resulting matrix
   * has the following properties: <code>a</code>=1, <code>b</code>=0,
   * <code>c</code>=0, <code>d</code>=1, <code>tx</code>=0,
   * <code>ty</code>=0.</p>
   *
   * <p>In matrix notation, the identity matrix looks like this:</p>
   *
   */
    Matrix.prototype.identity = function () {
        this._adaptee.identity();
    };
    /**
   * Includes parameters for scaling, rotation, and translation. When applied
   * to a matrix it sets the matrix's values based on those parameters.
   *
   * <p>Using the <code>createBox()</code> method lets you obtain the same
   * matrix as you would if you applied the <code>identity()</code>,
   * <code>rotate()</code>, <code>scale()</code>, and <code>translate()</code>
   * methods in succession. For example, <code>mat1.createBox(2,2,Math.PI/4,
   * 100, 100)</code> has the same effect as the following:</p>
   *
   * @param scaleX   The factor by which to scale horizontally.
   * @param scaleY   The factor by which scale vertically.
   * @param rotation The amount to rotate, in radians.
   * @param tx       The number of pixels to translate(move) to the right
   *                 along the <i>x</i> axis.
   * @param ty       The number of pixels to translate(move) down along the
   *                 <i>y</i> axis.
   */
    Matrix.prototype.createBox = function (scaleX, scaleY, rotation, tx, ty) {
        if (rotation === void 0) { rotation = 0; }
        if (tx === void 0) { tx = 0; }
        if (ty === void 0) { ty = 0; }
        this._adaptee.createBox(scaleX, scaleY, rotation, tx, ty);
    };
    /**
   * Creates the specific style of matrix expected by the
   * <code>beginGradientFill()</code> and <code>lineGradientStyle()</code>
   * methods of the Graphics class. Width and height are scaled to a
   * <code>scaleX</code>/<code>scaleY</code> pair and the
   * <code>tx</code>/<code>ty</code> values are offset by half the width and
   * height.
   *
   * <p>For example, consider a gradient with the following
   * characteristics:</p>
   *
   * <ul>
   *   <li><code>GradientType.LINEAR</code></li>
   *   <li>Two colors, green and blue, with the ratios array set to <code>[0,
   * 255]</code></li>
   *   <li><code>SpreadMethod.PAD</code></li>
   *   <li><code>InterpolationMethod.LINEAR_RGB</code></li>
   * </ul>
   *
   * <p>The following illustrations show gradients in which the matrix was
   * defined using the <code>createGradientBox()</code> method with different
   * parameter settings:</p>
   *
   * @param width    The width of the gradient box.
   * @param height   The height of the gradient box.
   * @param rotation The amount to rotate, in radians.
   * @param tx       The distance, in pixels, to translate to the right along
   *                 the <i>x</i> axis. This value is offset by half of the
   *                 <code>width</code> parameter.
   * @param ty       The distance, in pixels, to translate down along the
   *                 <i>y</i> axis. This value is offset by half of the
   *                 <code>height</code> parameter.
   */
    Matrix.prototype.createGradientBox = function (width, height, rotation, tx, ty) {
        if (rotation === void 0) { rotation = 0; }
        if (tx === void 0) { tx = 0; }
        if (ty === void 0) { ty = 0; }
        this._adaptee.createGradientBox(width, height, rotation, tx, ty);
    };
    /**
   * Applies a rotation transformation to the Matrix object.
   *
   * <p>The <code>rotate()</code> method alters the <code>a</code>,
   * <code>b</code>, <code>c</code>, and <code>d</code> properties of the
   * Matrix object. In matrix notation, this is the same as concatenating the
   * current matrix with the following:</p>
   *
   * @param angle The rotation angle in radians.
   */
    Matrix.prototype.rotate = function (angle) {
        this._adaptee.rotate(angle);
    };
    /**
   * Translates the matrix along the <i>x</i> and <i>y</i> axes, as specified
   * by the <code>dx</code> and <code>dy</code> parameters.
   *
   * @param dx The amount of movement along the <i>x</i> axis to the right, in
   *           pixels.
   * @param dy The amount of movement down along the <i>y</i> axis, in pixels.
   */
    Matrix.prototype.translate = function (dx, dy) {
        this._adaptee.translate(dx, dy);
    };
    /**
   * Applies a scaling transformation to the matrix. The <i>x</i> axis is
   * multiplied by <code>sx</code>, and the <i>y</i> axis it is multiplied by
   * <code>sy</code>.
   *
   * <p>The <code>scale()</code> method alters the <code>a</code> and
   * <code>d</code> properties of the Matrix object. In matrix notation, this
   * is the same as concatenating the current matrix with the following
   * matrix:</p>
   *
   * @param sx A multiplier used to scale the object along the <i>x</i> axis.
   * @param sy A multiplier used to scale the object along the <i>y</i> axis.
   */
    Matrix.prototype.scale = function (sx, sy) {
        this._adaptee.scale(sx, sy);
    };
    /**
   * Given a point in the pretransform coordinate space, returns the
   * coordinates of that point after the transformation occurs. Unlike the
   * standard transformation applied using the <code>transformPoint()</code>
   * method, the <code>deltaTransformPoint()</code> method's transformation
   * does not consider the translation parameters <code>tx</code> and
   * <code>ty</code>.
   *
   * @param point The point for which you want to get the result of the matrix
   *              transformation.
   * @return The point resulting from applying the matrix transformation.
   */
    Matrix.prototype.deltaTransformPoint = function (point) {
        return new this.sec.flash.geom.Point(this._adaptee.deltaTransformPoint(point.adaptee));
    };
    /**
   * Returns the result of applying the geometric transformation represented by
   * the Matrix object to the specified point.
   *
   * @param point The point for which you want to get the result of the Matrix
   *              transformation.
   * @return The point resulting from applying the Matrix transformation.
   */
    Matrix.prototype.transformPoint = function (point) {
        return new this.sec.flash.geom.Point(this._adaptee.transformPoint(point.adaptee));
    };
    /**
   * Copies all of the matrix data from the source Point object into the
   * calling Matrix object.
   *
   * @param sourceMatrix The Matrix object from which to copy the data.
   */
    Matrix.prototype.copyFrom = function (sourceMatrix) {
        this._adaptee.copyFrom(sourceMatrix.adaptee);
    };
    /**
   * Sets the members of Matrix to the specified values.
   *
   * @param a  The value that affects the positioning of pixels along the
   *           <i>x</i> axis when scaling or rotating an image.
   * @param b  The value that affects the positioning of pixels along the
   *           <i>y</i> axis when rotating or skewing an image.
   * @param c  The value that affects the positioning of pixels along the
   *           <i>x</i> axis when rotating or skewing an image.
   * @param d  The value that affects the positioning of pixels along the
   *           <i>y</i> axis when scaling or rotating an image..
   * @param tx The distance by which to translate each point along the <i>x</i>
   *           axis.
   * @param ty The distance by which to translate each point along the <i>y</i>
   *           axis.
   */
    Matrix.prototype.setTo = function (a, b, c, d, tx, ty) {
        this._adaptee.setTo(a, b, c, d, tx, ty);
    };
    /**
   * Copies specific row of the calling Matrix object into the Vector3D object.
   * The w element of the Vector3D object will not be changed.
   *
   * @param row      The row from which to copy the data from.
   * @param vector3D The Vector3D object from which to copy the data.
   */
    Matrix.prototype.copyRowTo = function (row, vector3D) {
        this._adaptee.copyRowTo(row, vector3D.adaptee);
    };
    /**
   * Copies specific column of the calling Matrix object into the Vector3D
   * object. The w element of the Vector3D object will not be changed.
   *
   * @param column   The column from which to copy the data from.
   * @param vector3D The Vector3D object from which to copy the data.
   */
    Matrix.prototype.copyColumnTo = function (column, vector3D) {
        this._adaptee.copyColumnTo(column, vector3D.adaptee);
    };
    /**
     * Copies a Vector3D object into specific row of the calling Matrix object.
     *
     * @param row      The row from which to copy the data from.
     * @param vector3D The Vector3D object from which to copy the data.
     */
    Matrix.prototype.copyRowFrom = function (row, vector3D) {
        this._adaptee.copyRowFrom(row, vector3D.adaptee);
    };
    /**
     * Copies a Vector3D object into specific column of the calling Matrix3D
     * object.
     *
     * @param column   The column from which to copy the data from.
     * @param vector3D The Vector3D object from which to copy the data.
     */
    Matrix.prototype.copyColumnFrom = function (column, vector3D) {
        this._adaptee.copyColumnFrom(column, vector3D.adaptee);
    };
    /**
     * Returns a new Matrix object that is a clone of this matrix, with an exact
     * copy of the contained object.
     *
     * @return A Matrix object.
     */
    Matrix.prototype.clone = function () {
        return new this.sec.flash.geom.Matrix(this._adaptee.clone());
    };
    Matrix.prototype.toString = function () {
        var m = this._adaptee.rawData;
        return '(a=' + m[0] + ', b=' + m[1] + ', c=' + m[2] + ', d=' + m[3] + ', tx=' + m[4] + ', ty=' + m[5] + ')';
    };
    // Keep in sync with static FromDataBuffer above!
    Matrix.prototype.writeExternal = function (output) {
        var m = this._adaptee.rawData;
        output.writeFloat(m[0]);
        output.writeFloat(m[1]);
        output.writeFloat(m[2]);
        output.writeFloat(m[3]);
        output.writeFloat(m[4]);
        output.writeFloat(m[5]);
    };
    Matrix.classSymbols = null; // [];
    Matrix.instanceSymbols = null; // ["a", "b", "c", "d", "tx", "ty", "concat",
    return Matrix;
}(ASObject));
export { Matrix };
